/**
 * conversationFiles — ce qu'une pièce de la messagerie devient avant de partir : son nom, son type,
 * et la partie du formulaire qui la porte.
 *
 * Ports de `CommonChat.js` (`pickFile`, `onSendImage`, `onSendPdf`, `uploadPart`). La lecture d'un
 * PDF et le `File` derrière une adresse sont ceux de l'ancien fil, rangés dans `data/utility`
 * (`pdfHelpers`, `fileFromURI`) pour que les deux lisent la même copie.
 */
import moment from "moment"
import { Platform } from "react-native"

import { fileFromURI } from "../../../../../../data/utility/fileFromURI"
import { isPdfFile as isPdfFileOf } from "../../../../../../data/utility/pdfHelpers"
import { reportUnreadableFile } from "./reportUnreadableFile"

/** Un fichier choisi : son adresse locale, et ce qu'il dit de lui quand il le dit. */
export type PickedFile = { uri: string; name?: string | null; mimeType?: string | null }

/** Ce que le formulaire envoie : sur mobile, l'adresse que `FormData` lit elle-même. */
export type FilePart = { uri: string; name: string; type: string }

/** `pdfHelpers.isPdfFile` : le type que porte le fichier, son nom, puis son adresse. */
export function isPdfFile(file: PickedFile | null | undefined): boolean {
    return !!isPdfFileOf(file)
}

const today = () => moment().format("YYYY-MM-DD")

/** Une photo (appareil, galerie) : `Masteur_Image_<nom de l'élève>_<date>.png`, comme `onSendImage`. */
export function imagePart(uri: string, displayName: string | null | undefined): FilePart {
    return { uri, name: `Masteur_Image_${displayName ?? ""}_${today()}.png`, type: "image/png" }
}

/** Un document choisi : son nom sans extension, sa date, et `.pdf` ou `.png` — comme `pickFile`. */
export function documentPart(file: PickedFile): FilePart {
    const pdf = isPdfFile(file)
    const base = (file.name ?? "document").split(".")[0]?.replace(/[\s.]+/g, "_") ?? "document"
    return {
        uri: file.uri,
        name: `${base}_${today()}.${pdf ? "pdf" : "png"}`,
        type: pdf ? "application/pdf" : "image/png",
    }
}

/**
 * La partie du formulaire : sur le web, le `File` derrière l'adresse (`blob:` d'un sélecteur,
 * `data:` de l'appareil photo) ; sur mobile, l'adresse elle-même. `null` quand le fichier ne se lit
 * plus (une adresse `blob:` meurt au rechargement) : signalé à Bugsnag, comme `CommonChat.uploadPart`.
 */
export async function uploadPart(part: FilePart): Promise<FilePart | File | null> {
    if (Platform.OS !== "web") return part
    try {
        return (await fileFromURI(part.uri, part.name, part.type)) as File
    } catch (error) {
        reportUnreadableFile(error, part.uri)
        return null
    }
}

/** Le formulaire d'un envoi de pièce : `temporary_id` et la pièce, sous `image` ou `document`. */
export function attachmentForm(field: "image" | "document", part: FilePart | File): FormData {
    const form = new FormData()
    form.append("temporary_id", "test")
    // Sur mobile, `FormData` prend l'objet `{ uri, name, type }` de React Native.
    form.append(field, part as unknown as Blob)
    return form
}
